import { Link } from "@inertiajs/react"
import { ArrowLeft, PackageSearch } from "lucide-react"

import { StaffLayout } from "@/components/layouts/staff-layout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { PurchaseForm, type PurchaseFormProps } from "@/features/staff/purchasing/purchase-form"
import { PURCHASING_PATHS, tPurchasing } from "@/features/staff/purchasing/purchasing"
import { t } from "@/lib/i18n"

const PAGE_SCOPE = "ui.react.pages.staff.location.manage.purchases.new"

/**
 * Two different empties, two different sentences.
 *
 * A restaurant with no suppliers and one with no supplies are both handed a form
 * that cannot be submitted, and they need different instructions — the first
 * points at the suppliers screen, the second at inventory. Rendering one blank
 * form with a disabled button for both is how a manager concludes the screen is
 * broken. This is the same split `Staff::Location::ReportsController` makes
 * between "no shifts yet" and "no sales in this window".
 */
export default function StaffLocationManagePurchasesNew({
  suppliers,
  supplies,
  currency,
}: PurchaseFormProps) {
  const blocked = suppliers.length === 0 || supplies.length === 0

  return (
    <StaffLayout>
      <MainContainer size="md">
        <div className="space-y-6">
          <PageHeader
            title={t(`${PAGE_SCOPE}.title`)}
            description={t(`${PAGE_SCOPE}.description`)}
            actions={
              <Button asChild variant="outline" data-testid="purchase-new-back">
                <Link href={PURCHASING_PATHS.purchases}>
                  <ArrowLeft className="size-4" />
                  {tPurchasing("back_to_purchases")}
                </Link>
              </Button>
            }
          />

          {blocked ? (
            <Card data-testid="purchase-new-blocked">
              <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
                <PackageSearch className="text-muted-foreground size-8" aria-hidden="true" />
                <p className="text-sm font-semibold">
                  {suppliers.length === 0
                    ? tPurchasing("blocked_no_suppliers_title")
                    : tPurchasing("blocked_no_supplies_title")}
                </p>
                <p className="text-muted-foreground max-w-md text-sm">
                  {suppliers.length === 0
                    ? tPurchasing("blocked_no_suppliers_hint")
                    : tPurchasing("blocked_no_supplies_hint")}
                </p>
                {suppliers.length === 0 && (
                  <Button asChild data-testid="purchase-new-go-suppliers">
                    <Link href={PURCHASING_PATHS.suppliers}>
                      {tPurchasing("blocked_go_suppliers")}
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <PurchaseForm suppliers={suppliers} supplies={supplies} currency={currency} />
          )}
        </div>
      </MainContainer>
    </StaffLayout>
  )
}
